import type { ErrorEvent, Event, EventHint } from "@sentry/core";

export const REDACTED = "[redacted]";

const EXTENSION_ORIGIN_PREFIXES = [
  "chrome-extension://",
  "moz-extension://",
  "safari-web-extension://",
  "safari-extension://",
];

// Extensions that inject a page script (crypto wallets, in particular) leave
// the frame without a resolvable source URL, so Sentry reports it under the
// `app:///` prefix it uses for unresolved paths and only the bundle name
// survives. These names belong to injected extension scripts and to nothing
// either app serves, so they identify the frame on their own — the
// no-public-asset-collision test is what keeps that second half true.
const INJECTED_BUNDLE_FILENAMES = new Set([
  "extensionPageScript.js",
  "inpage.js",
  "injected.js",
]);

const isExtensionLocation = (location: string): boolean =>
  EXTENSION_ORIGIN_PREFIXES.some((prefix) => location.startsWith(prefix)) ||
  INJECTED_BUNDLE_FILENAMES.has(location.slice(location.lastIndexOf("/") + 1));

const isExtensionFrame = (frame: {
  filename?: string | null;
  abs_path?: string | null;
}): boolean =>
  [frame.filename, frame.abs_path].some(
    (location) => typeof location === "string" && isExtensionLocation(location)
  );

// Sentry reports an anonymous frame's filename as `""`, which `??` would let
// mask a real `abs_path`.
const locatedFramesOf = (event: ErrorEvent) =>
  event.exception?.values
    ?.flatMap((value) => value.stacktrace?.frames ?? [])
    .filter((frame) => Boolean(frame.filename) || Boolean(frame.abs_path));

/**
 * Returns true only when every located stack frame belongs to browser-extension
 * code. Events with no usable frame locations are kept because their origin
 * cannot be identified safely.
 *
 * This cannot be Sentry's `denyUrls`: that matches any frame in the stack, so it
 * would also drop a real failure of ours that merely passed through an
 * extension frame.
 */
export const isExtensionOriginError = (event: ErrorEvent): boolean => {
  const locatedFrames = locatedFramesOf(event);
  if (!locatedFrames?.length) {
    return false;
  }
  return locatedFrames.every(isExtensionFrame);
};

// CEFSharp's host bridge, rejecting when it finds no matching element. Anchored
// whole: unanchored would drop a first-party error quoting this text, and the
// SDK prefix alone matches every primitive rejection in both apps.
const NOISE_MESSAGE_PATTERNS = [
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
];

// Google's token-refresh failure, verbatim from production. It sits on the noise
// side of the `isKnownNoiseEvent` line because the rejection is frameless: it
// names no call site of ours, so nothing in it points at code we could fix. A
// broken re-auth path surfaces as the request failure that follows it.
//
// Compared whole rather than on `status` alone: `captureException` on a caught
// non-`Error` also populates `extra.__serialized__`, which a looser match would
// reach.
const GOOGLE_TOKEN_REFRESH_PAYLOAD = {
  code: 400,
  message: "TOKEN_EXPIRED",
  status: "INVALID_ARGUMENT",
};

/**
 * Only fires when no frame could attribute the event to us — that check is what
 * makes reading a payload's vocabulary for provenance safe.
 */
export const isGoogleTokenRefreshRejection = (event: ErrorEvent): boolean => {
  if (locatedFramesOf(event)?.length) {
    return false;
  }
  const serialized = event.extra?.__serialized__;
  if (!isRecord(serialized)) {
    return false;
  }
  const expected = Object.entries(GOOGLE_TOKEN_REFRESH_PAYLOAD);
  return (
    Object.keys(serialized).length === expected.length &&
    expected.every(([key, value]) => serialized[key] === value)
  );
};

// linked-errors prepends causes, so the throw site is last — the entry Sentry's
// own `getPossibleEventMessages` reads.
const messagesOf = (event: ErrorEvent): string[] =>
  [event.message, event.exception?.values?.at(-1)?.value].filter(
    (message): message is string => typeof message === "string"
  );

const isKnownNoiseMessage = (event: ErrorEvent): boolean =>
  !locatedFramesOf(event)?.length &&
  messagesOf(event).some((message) =>
    NOISE_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );

/**
 * Noise we can name a source for. Not "is third party": Stripe.js failing to
 * load names its own source and is a real product symptom, so it is kept.
 *
 * The message match lives here rather than in `ignoreErrors` so that wiring this
 * one predicate into `beforeSend` is the whole integration.
 */
export const isKnownNoiseEvent = (event: ErrorEvent): boolean =>
  isExtensionOriginError(event) ||
  isGoogleTokenRefreshRejection(event) ||
  isKnownNoiseMessage(event);

const SENSITIVE_HEADER_NAMES = new Set([
  "cookie",
  "set-cookie",
  "authorization",
  // Inbound service-to-service callers authenticate with a shared static
  // secret in this header rather than a per-user bearer token, so a single
  // captured value authorizes every future call until someone rotates it.
  "x-api-key",
]);

// OAuth/OIDC callbacks arrive as ?code=...&state=..., and these values are
// sufficient for session replay or account takeover on their own.
const SENSITIVE_QUERY_PARAMS = new Set([
  "code",
  "state",
  "token",
  "access_token",
  "refresh_token",
  "id_token",
  "id_token_hint",
  "client_secret",
  "authorization",
  "session_state",
  "login_challenge",
  // Ory forwards the original OAuth2 request URL through this param, which
  // can itself carry a nested code/state — redact wholesale rather than
  // trying to recursively decode and re-scrub an arbitrary nested URL.
  "return_to",
  // Stripe appends these to the return_url of a redirect-based payment
  // confirmation and the append cannot be disabled. Each authorizes reading and
  // confirming the intent it belongs to.
  "payment_intent_client_secret",
  "setup_intent_client_secret",
  // The dashboard's former checkout search-param names for the same secrets. A
  // bookmark or long-open tab predating that change still reaches Sentry through
  // the URL or the referrer. Spelled lowercase because every lookup below folds
  // the incoming key, so the original camelCase would never match.
  "paymentintentsecret",
  "setupintentsecret",
  "customersessionsecret",
]);

/**
 * Returns a copy of a request's search params with the credential-bearing ones
 * replaced by {@link REDACTED}, for attaching to a Sentry event as `extra`.
 *
 * `beforeSend` scrubbing only reaches `event.request`, breadcrumbs, spans and
 * contexts, so params a call site copies onto `extra` itself would otherwise
 * ship verbatim. Key names are always preserved: knowing which params arrived
 * is what makes an event diagnosable, and only the values are secret.
 */
export function redactSearchParams(
  params: Record<string, string | string[] | undefined>
): Record<string, string | string[]> {
  const redacted: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }
    if (!SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      redacted[key] = value;
      continue;
    }
    redacted[key] = Array.isArray(value) ? value.map(() => REDACTED) : REDACTED;
  }
  return redacted;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function redactHeaders(headers: Record<string, unknown>): void {
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADER_NAMES.has(key.toLowerCase())) {
      headers[key] = REDACTED;
    }
  }
}

function redactQueryParamsRecord(query: Record<string, unknown>): void {
  for (const key of Object.keys(query)) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      query[key] = REDACTED;
    }
  }
}

// Handles both `?a=b` query strings and `#a=b` fragments (OAuth implicit
// flow puts access_token in the fragment) — URLSearchParams doesn't care
// which delimiter introduced the pairs, only that one was stripped first.
function redactSearchString(search: string): string {
  const prefix =
    search.startsWith("?") || search.startsWith("#") ? search[0] : "";
  const params = new URLSearchParams(prefix ? search.slice(1) : search);
  let mutated = false;
  for (const key of params.keys()) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      params.set(key, REDACTED);
      mutated = true;
    }
  }
  if (!mutated) {
    return search;
  }
  return `${prefix}${params.toString()}`;
}

// Masks sensitive query/fragment params wherever they appear in a URL
// string, absolute or relative — Sentry's request-data integration reports
// the raw incoming URL, so a hit on an OAuth callback route carries the same
// secrets as its query string or (implicit-flow) fragment.
function redactUrlString(rawUrl: string): string {
  const hashStart = rawUrl.indexOf("#");
  const beforeHash = hashStart === -1 ? rawUrl : rawUrl.slice(0, hashStart);
  const hash =
    hashStart === -1 ? "" : redactSearchString(rawUrl.slice(hashStart));

  const queryStart = beforeHash.indexOf("?");
  if (queryStart === -1) {
    return `${beforeHash}${hash}`;
  }
  const base = beforeHash.slice(0, queryStart);
  const query = redactSearchString(beforeHash.slice(queryStart));
  return `${base}${query}${hash}`;
}

function scrubRequest(request: Record<string, unknown>): void {
  // The parsed cookie map is entirely session/credential material.
  if (request.cookies !== undefined) {
    request.cookies = REDACTED;
  }

  if (isRecord(request.headers)) {
    redactHeaders(request.headers);
  }

  if (typeof request.url === "string") {
    request.url = redactUrlString(request.url);
  }

  if (typeof request.query_string === "string") {
    request.query_string = redactSearchString(request.query_string);
  } else if (isRecord(request.query_string)) {
    // The array-of-pairs form of QueryParams isn't handled: none of the
    // Sentry SDKs used in this repo emit it, only strings or plain objects.
    redactQueryParamsRecord(request.query_string);
  }
}

// Breadcrumb fields that carry a full URL (fetch/xhr/navigation) vs. just
// the query or fragment portion (Node's outgoing-request breadcrumb splits
// them out separately as http.query / http.fragment).
const URL_BREADCRUMB_KEYS = new Set(["url", "from", "to"]);
const SEARCH_BREADCRUMB_KEYS = new Set(["http.query", "http.fragment"]);

function scrubBreadcrumbData(data: Record<string, unknown>): void {
  for (const key of Object.keys(data)) {
    if (SENSITIVE_HEADER_NAMES.has(key.toLowerCase())) {
      data[key] = REDACTED;
      continue;
    }
    const value = data[key];
    if (isRecord(value)) {
      redactHeaders(value);
      continue;
    }
    if (typeof value !== "string") {
      continue;
    }
    const lowerKey = key.toLowerCase();
    if (URL_BREADCRUMB_KEYS.has(lowerKey)) {
      data[key] = redactUrlString(value);
    } else if (SEARCH_BREADCRUMB_KEYS.has(lowerKey)) {
      data[key] = redactSearchString(value);
    }
  }
}

// Sentry's browser/OpenTelemetry-based tracing (identity-ui's server/edge
// configs run at tracesSampleRate: 1) stores the raw request URL on each
// span as one of these attributes — the same secrets scrubbed off
// event.request would otherwise still ship via the trace/span payload.
const URL_SPAN_ATTRIBUTE_KEYS = new Set([
  "url",
  "http.target",
  "http.url",
  "url.full",
]);
const SEARCH_SPAN_ATTRIBUTE_KEYS = new Set([
  "url.query",
  "http.query",
  "url.fragment",
  "http.fragment",
]);
const HTTP_REQUEST_HEADER_PREFIX = "http.request.header.";
// Span attributes that are wholly credential material, so there is nothing
// worth preserving inside them the way there is for a URL.
const CREDENTIAL_SPAN_ATTRIBUTE_KEYS = new Set(["http.request.cookie"]);

/**
 * Redacts credential-bearing OpenTelemetry span attributes in place.
 *
 * Tracing instrumentation that copies request headers onto a span captures
 * whatever authenticates the request along with them, so this runs on any
 * span-shaped payload before it leaves the process — a Sentry event, or a
 * span on its way to an OTLP exporter.
 */
export function scrubSpanAttributes(data: Record<string, unknown>): void {
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (typeof value !== "string") {
      continue;
    }
    const lowerKey = key.toLowerCase();
    if (URL_SPAN_ATTRIBUTE_KEYS.has(lowerKey)) {
      data[key] = redactUrlString(value);
    } else if (SEARCH_SPAN_ATTRIBUTE_KEYS.has(lowerKey)) {
      data[key] = redactSearchString(value);
    } else if (
      CREDENTIAL_SPAN_ATTRIBUTE_KEYS.has(lowerKey) ||
      (lowerKey.startsWith(HTTP_REQUEST_HEADER_PREFIX) &&
        SENSITIVE_HEADER_NAMES.has(
          lowerKey.slice(HTTP_REQUEST_HEADER_PREFIX.length)
        ))
    ) {
      data[key] = REDACTED;
    }
  }
}

// App error boundaries commonly attach the current URL to a custom Sentry
// context (e.g. scope.setContext("routeError", { pathname, search, hash }))
// under whatever name they choose, so this matches by field name rather
// than by context name.
const URL_CONTEXT_KEYS = new Set(["url", "href"]);
const SEARCH_CONTEXT_KEYS = new Set(["search", "query", "query_string"]);
const FRAGMENT_CONTEXT_KEYS = new Set(["hash", "fragment"]);

function scrubContextValue(context: Record<string, unknown>): void {
  for (const key of Object.keys(context)) {
    const value = context[key];
    if (typeof value !== "string") {
      continue;
    }
    const lowerKey = key.toLowerCase();
    if (URL_CONTEXT_KEYS.has(lowerKey)) {
      context[key] = redactUrlString(value);
    } else if (
      SEARCH_CONTEXT_KEYS.has(lowerKey) ||
      FRAGMENT_CONTEXT_KEYS.has(lowerKey)
    ) {
      context[key] = redactSearchString(value);
    }
  }
}

/**
 * Strips session cookies, auth headers, and OAuth/OIDC query secrets from a
 * Sentry event before it leaves the process — `sendDefaultPii` would
 * otherwise ship live credentials to anyone with Sentry-project access.
 *
 * Generic over `Event` so the same function wires into `beforeSend`,
 * `beforeSendTransaction`, and the same request/URL data duplicated onto
 * the root span (`event.contexts.trace.data`), child spans
 * (`event.spans[].data`), and any other custom context an app attaches the
 * current URL to (e.g. a React error boundary).
 */
function scrubBreadcrumbs(breadcrumbs: unknown[]): void {
  for (const breadcrumb of breadcrumbs) {
    if (isRecord(breadcrumb) && isRecord(breadcrumb.data)) {
      scrubBreadcrumbData(breadcrumb.data);
    }
  }
}

function scrubSpans(spans: unknown[]): void {
  for (const span of spans) {
    if (isRecord(span) && isRecord(span.data)) {
      scrubSpanAttributes(span.data);
    }
  }
}

function scrubContexts(contexts: Record<string, unknown>): void {
  for (const [name, context] of Object.entries(contexts)) {
    if (name !== "trace" && isRecord(context)) {
      scrubContextValue(context);
    }
  }
}

export function scrubSensitiveData<E extends Event>(
  event: E,
  _hint?: EventHint
): E {
  if (isRecord(event.request)) {
    scrubRequest(event.request);
  }

  if (Array.isArray(event.breadcrumbs)) {
    scrubBreadcrumbs(event.breadcrumbs);
  }

  const traceData = event.contexts?.trace?.data;
  if (isRecord(traceData)) {
    scrubSpanAttributes(traceData);
  }

  if (Array.isArray(event.spans)) {
    scrubSpans(event.spans);
  }

  if (isRecord(event.contexts)) {
    scrubContexts(event.contexts);
  }

  return event;
}
